import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkle, X, ArrowRight } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const STORAGE_KEY = "flycher:seen-first-visit-nudge";

/**
 * Non-intrusive first-visit coachmark.
 * - Only shows when localStorage flag is missing.
 * - Slides in ~1.5s after landing, sits bottom-center on mobile, bottom-right on desktop.
 * - Permanently dismissed when user taps "Plan trip" CTA, clicks the X, OR navigates to /itinerary.
 */
export function FirstVisitNudge() {
  const [show, setShow] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Don't show on admin pages or on the itinerary page itself (no point).
    if (
      location.pathname.startsWith("/admin") ||
      location.pathname.startsWith("/itinerary") ||
      location.pathname.startsWith("/consultation") ||
      location.pathname.startsWith("/me/")
    ) {
      return;
    }
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      return;
    }
    const t = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const dismiss = (persist = true) => {
    setShow(false);
    if (persist) {
      try {
        localStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
    }
  };

  // Auto-dismiss when the user actually reaches /itinerary
  useEffect(() => {
    if (location.pathname.startsWith("/itinerary")) {
      dismiss(true);
    }
  }, [location.pathname]);

  const goPlan = () => {
    dismiss(true);
    navigate("/itinerary");
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="first-visit-nudge"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          // Mobile: above the bottom tab bar (which is 64px + safe-area inset).
          // Desktop: bottom-right corner.
          className="fixed left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-[9999]"
          style={{
            bottom: "calc(80px + env(safe-area-inset-bottom))",
          }}
        >
          <div
            className="rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-orange-200/30 p-4 sm:p-5 flex gap-3"
            style={{ borderLeft: "4px solid #FF7E4D" }}
          >
            <div
              className="w-10 h-10 rounded-xl grid place-items-center flex-shrink-0"
              style={{ background: "#FFF1E8" }}
            >
              <Sparkle size={20} weight="fill" color="#FF7E4D" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">
                New here?
              </p>
              <h3 className="font-serif text-base text-slate-900 mt-0.5 leading-snug">
                Tap{" "}
                <span style={{ color: "#FF7E4D" }} className="font-semibold">
                  Plan
                </span>{" "}
                to build your first AI itinerary
              </h3>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Day-by-day boards with stays, food &amp; deep-linked deals — in under a minute.
              </p>
              <button
                type="button"
                onClick={goPlan}
                data-testid="first-visit-nudge-cta"
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-white shadow-sm hover:shadow-md transition-shadow"
                style={{ background: "#FF7E4D" }}
              >
                Plan my trip <ArrowRight size={12} weight="bold" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => dismiss(true)}
              data-testid="first-visit-nudge-close"
              aria-label="Dismiss"
              className="self-start text-slate-400 hover:text-slate-700 transition-colors p-1 -mt-1 -mr-1"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
